import React, { useState, useEffect, useRef } from 'react';

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import Text from '../../components/Text';
import Input from '../../components/Input';
import Selects from '../../components/Select';
import Button from '../../components/Button';
import { useSnackbar } from '../../components/Snackbar';
import Card, { DefaultLoadingBodyComponent } from '../../components/Card';
import Table from '../../components/Table';
import Modal from '../../components/Modal';

import { removeNonDigits } from '../../utils/formatter';
import { FaTrash, FaPlus } from 'react-icons/fa';

import establishmentStore from '../../store/establishment';

import {
  validateTransaction,
  validateProduct,
  handleCreate,
  handleCreateByProduct,
  fetchAvailableProducts,
  validateFirstStep,
  getProductTransactionTypes,
} from './controller';

const KEYS = {
  DOT: 190,
  NUMPAD_DOT: 110,
};

export default function Transaction({ type }) {
  const cpfRef = useRef(null);
  const quantityRef = useRef(null);
  const selectRef = useRef(null);

  const snackbar = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [cpf, setCpf] = useState();
  const [errors, setErrors] = useState();
  const [token, setToken] = useState('');
  const [product, setProduct] = useState();
  const [loadingCard, setLoadingCard] = useState(false);
  const [productQuantity, setProductQuantity] = useState();
  const [availableProducts, setAvailableProducts] = useState();
  const [productsList, setProductsList] = useState([]);
  const [steps, setSteps] = useState(1);
  const [transactionType, setTransactionType] = useState(type);
  const [typeModal, setTypeModal] = useState({ show: false, selected: null });

  const currentEstablishment = establishmentStore(state => state.establishment);

  useEffect(() => {
    fetchAvailableProducts(setAvailableProducts);
  }, []);

  useEffect(() => {
    if (cpfRef?.current) {
      cpfRef.current.inputElement?.focus();
    }
  }, [cpfRef]);

  useEffect(() => {
    if (steps === 1 && cpfRef?.current) {
      cpfRef.current.inputElement?.focus();
    }
  }, [steps]);

  async function addProduct(overrideType) {
    const data = {
      product,
      productQuantity,
      transactionType: type || overrideType || transactionType,
    };
    if (!(await validateProduct(data, setErrors))) {
      return false;
    }
    if (!data.transactionType) {
      setTypeModal({ show: true, selected: null });
      return false;
    }
    if (
      productsList.some(
        item =>
          item.transactionType && item.transactionType !== data.transactionType
      )
    ) {
      snackbar.show(
        <Text>Todos os produtos da transação precisam ter o mesmo tipo</Text>,
        { type: 'error' }
      );
      return false;
    }
    if (productsList.some(elem => elem.product.id === product.id)) {
      const productIndex = productsList.findIndex(
        elem => elem.product.id === product.id
      );
      setProductsList(old => {
        const aux = [...old];
        aux[productIndex].productQuantity =
          Number(aux[productIndex].productQuantity) + Number(productQuantity);
        return aux;
      });

      setProduct('');
      setProductQuantity('');
      return true;
    }

    setProductsList(old => [...old, data]);
    setProduct('');
    setProductQuantity('');
    return true;
  }

  function handleRemoveProduct(index) {
    const aux = [...productsList];
    aux.splice(index, 1);

    setProductsList(aux);
  }

  async function createCreditTransaction() {
    if (productsList.length > 0) {
      const data = {
        cpf: cpf?.replace(/[^0-9]/g, ''),
        cnpj: currentEstablishment.cgccpf.replace(/[^0-9]/g, ''),
        type: type || transactionType || 'credit',
        productsList,
      };
      if (!(await validateTransaction(data, setErrors))) return;

      setLoading(true);
      setLoadingCard(true);

      try {
        const create = type ? handleCreate : handleCreateByProduct;
        const response = await create({
          products: productsList.map(item => {
            return {
              quantity: item.productQuantity,
              total_unit: 3,
              unit: item.product.unit_measure,
              name: item.product.name,
              points: item.product.points,
              id: item.product.id,
            };
          }),
          cpf: data.cpf,
          type: data.type,
          cnpj: data.cnpj,
        });

        if (!response.error) {
          setSteps(1);
          setCpf('');
          setProduct({});
          setProductQuantity(0);
          setProductsList([]);
          snackbar.show(<Text>Transação criada com sucesso</Text>, {
            type: 'success',
          });
        } else {
          snackbar.show(
            <Text>
              {response.error ? response.error : 'Erro ao criar transação'}
            </Text>,
            {
              type: 'error',
            }
          );
          throw new Error('Erro ao criar transação');
        }
      } catch (err) {
        console.log({ err });
        snackbar.show(<Text> Erro ao cadastrar transação </Text>, {
          type: 'error',
        });
      } finally {
        setLoading(false);
        setLoadingCard(false);
      }
    } else {
      snackbar.show(<Text>Precisa ser adicionado pelo menos um produto</Text>, {
        type: 'error',
      });
    }
  }

  async function handleEnterPress() {
    setErrors({});

    if (steps === 1) {
      const cpfValue = type
        ? removeNonDigits(cpf)
        : removeNonDigits(cpfRef.current?.inputElement?.value || cpf);

      if (
        !(await validateFirstStep(
          {
            cpf: cpfValue,
            type,
            token: removeNonDigits(token),
            newTransaction: !type,
          },
          setErrors
        ))
      ) {
        snackbar.show(<Text> Erro ao realizar operação</Text>, {
          type: 'error',
        });
        return false;
      }

      setSteps(2);
      quantityRef.current.value = '';

      return true;
    }

    if (steps === 2) {
      if (product && productQuantity) {
        if (
          !type &&
          getProductTransactionTypes(product).length > 1 &&
          !transactionType
        ) {
          setTypeModal({ show: true, selected: null });
          return true;
        }
        addProduct();

        if (selectRef?.current) {
          selectRef.current.select.focus();
        }
        return true;
      }
    }

    return false;
  }

  function chooseTransactionType(selected) {
    setTypeModal({ show: true, selected });
  }

  async function confirmTransactionType() {
    const { selected } = typeModal;
    setTransactionType(selected);
    setTypeModal({ show: false, selected: null });
    await addProduct(selected);
  }

  async function createDebitTransaction() {
    if (productsList.length > 0) {
      const data = {
        cpf: cpf?.replace(/[^0-9]/g, ''),
        cnpj: currentEstablishment.cgccpf.replace(/[^0-9]/g, ''),
        type: type || transactionType || 'debit',
        productsList,
      };

      if (!(await validateTransaction(data, setErrors))) return false;
      setLoading(true);
      setLoadingCard(true);

      try {
        const create = type ? handleCreate : handleCreateByProduct;
        const response = await create({
          products: productsList.map(item => {
            return {
              quantity: item.productQuantity,
              unit: item.product.unit_measure,
              name: item.product.name,
              points: item.product.points,
              id: item.product.id,
            };
          }),
          cpf: data.cpf,
          type: data.type,
          cnpj: data.cnpj,
          driver_token: Number(data.token),
        });

        if (!response.error) {
          snackbar.show(<Text>Transação de débito criada com sucesso</Text>, {
            type: 'success',
          });
        } else {
          snackbar.show(
            <Text>
              {response.error
                ? response.error
                : 'Erro ao criar transação de débito'}
            </Text>,
            {
              type: 'error',
            }
          );
        }
        setSteps(1);
        setCpf('');
        setProduct({});
        setProductQuantity(0);
        setToken('');
        setProductsList([]);

        return response;
      } catch (err) {
        snackbar.show(<Text> Erro ao cadastrar transação de débito </Text>, {
          type: 'error',
        });
        return null;
      } finally {
        setLoadingCard(false);
        setLoading(false);
      }
    } else {
      snackbar.show(<Text>Precisa ser adicionado pelo menos um produto</Text>, {
        type: 'error',
      });
      return null;
    }
  }

  useEffect(() => {
    const keyDownHandler = event => {
      if (event.key === 'Enter' && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        handleEnterPress();
      } else if ((event.ctrlKey || event.metaKey) && event.keyCode === 13) {
        event.preventDefault();
        if ((type || transactionType) === 'credit') createCreditTransaction();
        if ((type || transactionType) === 'debit') createDebitTransaction();
      }
    };

    document.addEventListener('keydown', keyDownHandler);

    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [cpf, product, productQuantity, selectRef, token, transactionType]);

  function Header() {
    return (
      <Text color="#464E5F" type="header">
        Nova transação no estabelecimento{' '}
        <Text
          color="#464E5F"
          type="header"
          style={{ fontWeight: '600', textDecoration: 'underline' }}
        >
          {currentEstablishment?.social_name}
        </Text>
      </Text>
    );
  }

  return (
    <>
      <Row>
        <Col xs={12}>
          <Card
            loading={loadingCard}
            LoadingBodyComponent={() => (
              <DefaultLoadingBodyComponent linesCount={1} perLine={1} />
            )}
            header={<Header />}
          >
            <Row className="d-flex align-items-center">
              {steps === 1 ? (
                <>
                  <Col xs={12} md={6}>
                    <Input
                      label="CPF *"
                      value={cpf}
                      autoFocus
                      onChange={event => setCpf(event.target.value)}
                      masked
                      ref={cpfRef}
                      mask={[
                        /[0-9]/,
                        /[0-9]/,
                        /[0-9]/,
                        '.',
                        /[0-9]/,
                        /[0-9]/,
                        /[0-9]/,
                        '.',
                        /[0-9]/,
                        /[0-9]/,
                        /[0-9]/,
                        '-',
                        /[0-9]/,
                        /[0-9]/,
                      ]}
                      error={errors?.cpf}
                    />
                  </Col>
                </>
              ) : (
                <>
                  <Col xs={12} md={6}>
                    <Selects
                      autoFocus
                      label="Produto *"
                      ref={selectRef}
                      options={availableProducts}
                      value={product}
                      getOptionLabel={option => option.name}
                      getOptionValue={option => option.id}
                      onChange={event => {
                        setProduct(event);
                        if (!type) {
                          const productTypes =
                            getProductTransactionTypes(event);
                          setTransactionType(
                            productTypes.length === 1
                              ? productTypes[0]
                              : undefined
                          );
                        }
                        if (quantityRef?.current) {
                          quantityRef.current?.focus();
                        }
                      }}
                      error={errors?.product}
                    />
                  </Col>
                  <Col xs={12} md={4}>
                    <Input
                      label={`Quantidade ${
                        product?.unit_measure ? `(${product.unit_measure})` : ''
                      } *`}
                      type="number"
                      hideAppearance
                      value={productQuantity}
                      onChange={event => {
                        setProductQuantity(event.target.value);
                      }}
                      ref={quantityRef}
                      containerStyle={{ marginBottom: 0 }}
                      error={errors?.productQuantity}
                      onKeyDown={event => {
                        if (
                          event.keyCode === KEYS.DOT ||
                          event.keyCode === KEYS.NUMPAD_DOT
                        )
                          event.preventDefault();
                      }}
                    />
                  </Col>
                  <Col>
                    <Button md={2} onClick={() => handleEnterPress()}>
                      <FaPlus />
                    </Button>
                  </Col>
                  {product && !!productQuantity && (
                    <Col xs={12} md={3} className="mb-3">
                      <Text>Pontos: {productQuantity * product?.points}</Text>
                    </Col>
                  )}
                  {productsList.length > 0 && (
                    <Col xs={12}>
                      <Table responsive>
                        <thead>
                          <tr>
                            <th>
                              <Text type="label" weight="500">
                                Produto
                              </Text>
                            </th>
                            <th className="text-center">
                              <Text type="label" weight="500">
                                Quantidade
                              </Text>
                            </th>
                            <th className="text-center">
                              <Text type="label" weight="500">
                                Pontos
                              </Text>
                            </th>
                            <th className="text-center">
                              <Text type="label" weight="500">
                                Tipo da transação
                              </Text>
                            </th>
                            <th className="text-center">
                              <Text type="label" weight="500">
                                Ação
                              </Text>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {productsList.map((item, index) => (
                            <tr>
                              <td>
                                <Text type="regular" weight="400">
                                  {item.product.name}
                                </Text>
                              </td>
                              <td width="240" className="text-center">
                                <Text type="regular">
                                  {item.productQuantity}
                                </Text>
                              </td>
                              <td width="240" className="text-center">
                                <Text type="regular">
                                  {item.productQuantity * item.product.points}
                                </Text>
                              </td>
                              <td width="180" className="text-center">
                                <Text type="regular">
                                  {item.transactionType === 'credit'
                                    ? 'Crédito'
                                    : item.transactionType === 'debit'
                                    ? 'Débito'
                                    : '-'}
                                </Text>
                              </td>
                              <td width="120" className="text-center">
                                <Button
                                  variant="error"
                                  size="sm"
                                  onClick={() => handleRemoveProduct(index)}
                                >
                                  <FaTrash />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Col>
                  )}
                </>
              )}
              <Col xs={12} md={12} className="mt-4 d-flex justify-content-end">
                {steps === 1 ? (
                  <Button
                    variant="secondary"
                    onClick={() => handleEnterPress()}
                  >
                    <Text>Próximo</Text>
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="secondary"
                      onClick={() => setSteps(1)}
                      className="mr-1"
                    >
                      <Text>Voltar</Text>
                    </Button>
                    <Button
                      onClick={() => {
                        if ((type || transactionType) === 'credit')
                          createCreditTransaction();
                        if ((type || transactionType) === 'debit')
                          createDebitTransaction();
                      }}
                      loading={loading}
                    >
                      <Text>Salvar</Text>
                    </Button>
                  </>
                )}
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
      <Modal
        show={typeModal.show}
        handleClose={() => setTypeModal({ show: false, selected: null })}
        heading={
          typeModal.selected
            ? 'Confirmar tipo da transação'
            : 'Escolha o tipo da transação'
        }
        body={
          typeModal.selected ? (
            <Text>
              Confirma uma transação de{' '}
              {typeModal.selected === 'credit' ? 'crédito' : 'débito'} para o
              produto <strong>{product?.name}</strong>?
            </Text>
          ) : (
            <Row>
              <Col xs={6}>
                <Button block onClick={() => chooseTransactionType('credit')}>
                  <Text>Crédito</Text>
                </Button>
              </Col>
              <Col xs={6}>
                <Button
                  block
                  variant="secondary"
                  onClick={() => chooseTransactionType('debit')}
                >
                  <Text>Débito</Text>
                </Button>
              </Col>
            </Row>
          )
        }
        footer={
          typeModal.selected && (
            <>
              <Button
                variant="secondary"
                onClick={() => setTypeModal({ show: true, selected: null })}
              >
                <Text>Voltar</Text>
              </Button>
              {/* eslint-disable-next-line react/jsx-no-bind */}
              <Button onClick={confirmTransactionType}>
                <Text>Confirmar</Text>
              </Button>
            </>
          )
        }
      />
    </>
  );
}
